import { Cloud, HardDrive } from "lucide-react";
import type { Trip } from "../lib/api.ts";
import { useApi } from "../lib/useApi.ts";
import { Card, EmptyState, ICON, PageState, Pill, SourceRow } from "../ui/index.ts";

type StorageNode = { id: string; kind: string; state: string };

/** Where the trip's originals are kept. The default destination is created with the trip. */
export function StoragePage({ trip }: { trip: Trip }) {
  const nodes = useApi<{ items: StorageNode[] }>(`/v1/trips/${trip.id}/storage-nodes`);
  return (
    <PageState
      {...nodes}
      isEmpty={(d) => d.items.length === 0}
      empty={
        <EmptyState
          icon={<HardDrive size={24} strokeWidth={1.5} />}
          title="No storage destination"
          body="New trips get one automatically. For an older trip, ask the platform owner to run the storage repair."
        />
      }
    >
      {(d) => (
        <Card title="Storage destinations">
          {d.items.map((n) => (
            <SourceRow
              key={n.id}
              icon={<Cloud {...ICON} />}
              name={n.kind === "S3" || n.kind === "R2" ? "Cloud hosted" : "Self hosted"}
              detail={
                <Pill tone={n.state === "ACTIVE" ? "good" : "wait"}>
                  {n.state === "ACTIVE" ? "Active" : "Disabled"}
                </Pill>
              }
            />
          ))}
          <p className="muted small" style={{ margin: 0 }}>
            Every upload is read back in full and its checksum compared before it counts as
            verified. Nothing is offered for cleanup before that.
          </p>
        </Card>
      )}
    </PageState>
  );
}
